import { useCallback, useRef, useState } from 'react';

import {
    type CoverageRow,
    type WorkWeekStartDay,
} from '@apps/web/features/coverage/model/coverage_types';
import { buildExportTable } from '@apps/web/features/coverage/model/export_model';
import {
    requestErrorMessage,
    type RequestStatus,
} from '@apps/web/features/coverage/model/request_model';
import {
    buildCsv,
    buildXlsxBlob,
    CSV_BOM,
    reportFilename,
    triggerBlobDownload,
} from '@apps/web/lib/tabular_export';
import { type WorkforceAvailabilityResponse } from '@libraries/typescript/api_client';

export function useCoverageExports({
    data,
    rows,
    workWeekStartDay,
}: {
    readonly data: WorkforceAvailabilityResponse | undefined;
    readonly rows: ReadonlyArray<CoverageRow>;
    readonly workWeekStartDay: WorkWeekStartDay;
}) {
    const [status, setStatus] = useState<RequestStatus>('idle');
    const [error, setError] = useState<string | null>(null);
    const pendingRef = useRef(false);
    const run = useCallback(
        async (format: 'csv' | 'xlsx') => {
            if (pendingRef.current || !data) {
                return;
            }
            pendingRef.current = true;
            setStatus('pending');
            setError(null);
            try {
                const table = buildExportTable(rows, workWeekStartDay);
                const blob =
                    format === 'csv'
                        ? new Blob(
                              [CSV_BOM + buildCsv(table.columns, table.rows)],
                              { type: 'text/csv;charset=utf-8' },
                          )
                        : await buildXlsxBlob(table.columns, table.rows);
                triggerBlobDownload(
                    blob,
                    reportFilename(`coverage-${data.week_start}`, format),
                );
                setStatus('success');
            } catch (requestError) {
                setStatus('error');
                setError(
                    requestErrorMessage(
                        requestError,
                        `We couldn’t create the ${format.toUpperCase()} export.`,
                    ),
                );
            } finally {
                pendingRef.current = false;
            }
        },
        [data, rows, workWeekStartDay],
    );

    return {
        status,
        error,
        dismissError: () => {
            setStatus('idle');
            setError(null);
        },
        exportCsv: () => run('csv'),
        exportXlsx: () => run('xlsx'),
    };
}
